import type { CandidateEditableProfileDto, CandidateLocationDto, TalentNetworkSessionDto } from "@artha/api-contracts";
import { normalizeCountryCode, normalizeWhatsAppNumber, normalizeWorkMode } from "@artha/utils";
import { normalizePhoneInputValue } from "@artha/ui";
import type { CandidateProfileVerification, ResumeReviewForm } from "./personalization.types";
import { RESUME_WORK_MODES } from "./personalization.constants";

export {
  EMPTY_REVIEW_FORM,
  PERSONALIZATION_FIELD_CLASS_NAME,
} from "./personalization.constants";

const reviewWorkMode = (value: string) => {
  const normalized = normalizeWorkMode(value.trim().toLowerCase());
  return RESUME_WORK_MODES.find((mode) => mode === normalized) || "";
};

export const hasVerifiedTalentNetworkProfile = (
  session: TalentNetworkSessionDto | null,
  externalId: string,
  profile: CandidateEditableProfileDto,
) => Boolean(
  session?.authenticated &&
  session.externalId === externalId &&
  session.membershipStatus === "active" &&
  (session.verificationLevel === "dual_otp" || session.verificationLevel === "phone_only") &&
  session.editableProfile &&
  session.editableProfile.email.trim().toLowerCase() === profile.email.trim().toLowerCase() &&
  normalizeWhatsAppNumber(session.editableProfile.phone || "") === normalizeWhatsAppNumber(profile.phone || ""),
);

export const hasVerifiedCandidateEmail = (
  session: CandidateProfileVerification | null,
  externalId: string,
  email: string,
) => Boolean(
  session && (session.authenticated || "source" in session) && session.emailVerified &&
  session.externalId === externalId && session.editableProfile &&
  session.editableProfile.email.trim().toLowerCase() === email.trim().toLowerCase(),
);

export const hasVerifiedCandidatePhone = (
  session: CandidateProfileVerification | null,
  externalId: string,
  profile: CandidateEditableProfileDto,
) => Boolean(
  session && (session.authenticated || "source" in session) &&
  session.externalId === externalId && session.editableProfile &&
  ("phoneVerified" in session && session.phoneVerified !== undefined ? session.phoneVerified :
    "verificationLevel" in session &&
      (session.verificationLevel === "dual_otp" || session.verificationLevel === "phone_only")) &&
  profile.phone &&
  session.editableProfile.email.trim().toLowerCase() === profile.email.trim().toLowerCase() &&
  normalizeWhatsAppNumber(session.editableProfile.phone || "") === normalizeWhatsAppNumber(profile.phone),
);

export const candidateProfileFingerprint = (profile?: CandidateEditableProfileDto) =>
  JSON.stringify(profile, (_key, value) =>
    value && typeof value === "object" && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value).sort(([left], [right]) => left.localeCompare(right)))
      : value,
  );

export const toReviewForm = (
  profile: CandidateEditableProfileDto,
): ResumeReviewForm => ({
  name: profile.name,
  email: profile.email,
  phone: normalizePhoneInputValue(
    profile.phone,
    normalizeCountryCode(profile.location?.country || ""),
  ),
  titles: profile.titles.join(", "),
  skills: profile.skills.join(", "),
  keywords: profile.keywords.join(", "),
  experienceYears:
    typeof profile.experienceYears === "number"
      ? String(Math.round(profile.experienceYears))
      : "",
  educationLevel: profile.education?.[0]?.level || "",
  educationField: profile.education?.[0]?.field || "",
  educationInstitution: profile.education?.[0]?.institution || "",
  additionalEducation: profile.education?.slice(1) || [],
  educationRecords: profile.education,
  experience: profile.experience,
  projects: profile.projects,
  links: profile.links,
  city: profile.location?.city || "",
  state: profile.location?.state || "",
  country: profile.location?.country || "",
  latitude: profile.location?.latitude,
  longitude: profile.location?.longitude,
  placeId: profile.location?.placeId,
  workMode: reviewWorkMode(profile.workMode || ""),
});

const parseList = (value: string) => {
  const seen = new Set<string>();
  return value.split(",").reduce<string[]>((items, entry) => {
    const clean = entry.trim();
    const key = clean.toLowerCase();
    if (clean && !seen.has(key)) {
      seen.add(key);
      items.push(clean);
    }
    return items;
  }, []);
};

export const toReviewLocation = (form: ResumeReviewForm): CandidateLocationDto => ({
  city: form.city,
  state: form.state,
  country: form.country,
  ...(form.latitude !== undefined && form.longitude !== undefined
    ? { latitude: form.latitude, longitude: form.longitude }
    : {}),
  ...(form.placeId ? { placeId: form.placeId } : {}),
});

export const updateReviewLocation = (form: ResumeReviewForm, location: CandidateLocationDto): ResumeReviewForm => ({
  ...form,
  city: location.city || "",
  state: location.state || "",
  country: location.country || "",
  latitude: location.latitude,
  longitude: location.longitude,
  placeId: location.placeId,
});

export const toEditableProfile = (
  form: ResumeReviewForm,
): CandidateEditableProfileDto => ({
  name: form.name.trim(),
  email: form.email.trim().toLowerCase(),
  phone: normalizePhoneInputValue(form.phone, normalizeCountryCode(form.country)),
  titles: parseList(form.titles),
  skills: parseList(form.skills),
  keywords: parseList(form.keywords),
  ...(form.experienceYears.trim()
    ? { experienceYears: Math.round(Number(form.experienceYears)) }
    : {}),
  ...(form.educationRecords !== undefined ||
  form.educationLevel.trim() ||
  form.educationField.trim() ||
  form.educationInstitution.trim() ||
  form.additionalEducation?.length
    ? {
        education: [
          {
            ...Object.fromEntries(Object.entries(form.educationRecords?.[0] || {}).filter(([key]) => !["level", "field", "institution"].includes(key))),
            ...(form.educationLevel.trim() ? { level: form.educationLevel.trim() } : {}),
            ...(form.educationField.trim() ? { field: form.educationField.trim() } : {}),
            ...(form.educationInstitution.trim() ? { institution: form.educationInstitution.trim() } : {}),
          },
          ...(form.additionalEducation || []),
        ].filter((entry) => entry.level || entry.field || entry.institution),
      }
    : {}),
  ...(form.experience !== undefined ? { experience: form.experience } : {}),
  ...(form.projects !== undefined ? { projects: form.projects } : {}),
  ...(form.links !== undefined ? { links: form.links } : {}),
  ...(form.city.trim() || form.state.trim() || form.country.trim() || (form.latitude !== undefined && form.longitude !== undefined)
    ? {
        location: {
          ...(form.city.trim() ? { city: form.city.trim() } : {}),
          ...(form.state.trim() ? { state: form.state.trim() } : {}),
          ...(form.country.trim() ? { country: form.country.trim() } : {}),
          ...(form.latitude !== undefined && form.longitude !== undefined
            ? { latitude: form.latitude, longitude: form.longitude }
            : {}),
          ...(form.placeId ? { placeId: form.placeId } : {}),
        },
      }
    : {}),
  ...(reviewWorkMode(form.workMode)
    ? { workMode: reviewWorkMode(form.workMode) }
    : {}),
});
